import { Fragment, useEffect, useState } from "react";
import { Keyboard, Platform, View } from "@-/com.npmjs.react-native";
import { Text } from "@-/com.npmjs.react-native/text";
import { FullWindowOverlay } from "@-/com.npmjs.react-native-screens";
import { nativeDevBuildOverlay } from "@-/dev.improve.haniverse.modules.dev-build-overlay";
import { BUNDLE_GIT_COMMIT } from "@-/dev.improve.haniverse.lib.app.build-info";
import { getAppVersionLabel } from "@-/dev.improve.haniverse.lib.app.version";
import { useDeviceSetting } from "@-/dev.improve.haniverse.lib.device-settings";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { useHasHydrated } from "@-/dev.improve.haniverse.lib.hydration";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { useProcessStatsSample } from "@-/dev.improve.haniverse.lib.diagnostics";
import { useBundleRevisionId } from "#use-bundle-revision-id";
import { useLastHotReload } from "#use-last-hot-reload";
import { useMemoryFootprint } from "#use-memory-footprint";
import { useNativeBuildStatus } from "#use-native-build-status";

const BUNDLE_LOADED_AT = __DEV__ ? new Date().toLocaleTimeString("en-US", { hour12: false }) : "";
const FallbackOverlay = Platform.OS === "ios" ? FullWindowOverlay : Fragment;

function formatMegabytes(bytes: number | null): string | null {
  return bytes === null ? null : `${(bytes / 1024 / 1024).toFixed(0)}M`;
}

function formatMegabytesLabel(bytes: number | null): string | null {
  return bytes === null ? null : `${(bytes / 1024 / 1024).toFixed(0)} MB`;
}

type DevBuildFooterStamp = {
  accessibilityLabel: string;
  outdated: boolean;
  text: string;
};

function useDevBuildFooterStamp(): DevBuildFooterStamp {
  const revision = useBundleRevisionId();
  const refreshedAt = useLastHotReload();
  const memory = useMemoryFootprint(true);
  const status = useNativeBuildStatus();
  const stats = useProcessStatsSample(true);

  // Release builds have no Metro revision / hot reload / native-build freshness to show, so the
  // stamp opens with the store version and the bundle's commit instead.
  const releaseBuildLabel = `${getAppVersionLabel()}${BUNDLE_GIT_COMMIT.length > 0 ? ` ${BUNDLE_GIT_COMMIT.slice(0, 7)}` : ""}`;
  const cpu = stats.cpuPercent === null ? null : `${stats.cpuPercent.toFixed(0)}%`;
  const memoryText = formatMegabytes(memory ?? stats.memoryFootprintBytes);
  const jsHeap = formatMegabytes(stats.jsHeapBytes);
  const uiLatency =
    stats.mainThreadLatencyMs === null ? null : `${stats.mainThreadLatencyMs.toFixed(0)}ms`;

  const text = [
    ...(__DEV__
      ? [
          status.label,
          `◇ ${(revision ?? "…").slice(0, 8)}`,
          `◷ ${BUNDLE_LOADED_AT}`,
          `↻ ${refreshedAt ?? "—"}`,
        ]
      : [`▣ ${releaseBuildLabel}`]),
    cpu === null ? "" : `cpu ${cpu}`,
    memoryText === null ? "" : `▤ ${memoryText}`,
    jsHeap === null ? "" : `js ${jsHeap}`,
    uiLatency === null ? "" : `ui ${uiLatency}`,
  ]
    .filter(Boolean)
    .join(" · ");
  const accessibilityLabel = [
    __DEV__
      ? `${status.text}, JS ${(revision ?? "pending").slice(0, 8)} · loaded ${BUNDLE_LOADED_AT} · refreshed ${refreshedAt ?? "—"}`
      : `build ${releaseBuildLabel}`,
    cpu === null ? "" : `cpu ${cpu}`,
    formatMegabytesLabel(memory ?? stats.memoryFootprintBytes) === null
      ? ""
      : `memory ${formatMegabytesLabel(memory ?? stats.memoryFootprintBytes)}`,
    formatMegabytesLabel(stats.jsHeapBytes) === null
      ? ""
      : `js heap ${formatMegabytesLabel(stats.jsHeapBytes)}`,
    uiLatency === null ? "" : `ui thread ${uiLatency}`,
  ]
    .filter(Boolean)
    .join(" · ");

  return { accessibilityLabel, outdated: status.outdated, text };
}

/**
 * Drives the native touch-through overlay window (when the binary has the module) and reports
 * whether it failed, so the React fallback can take over.
 */
function useDevBuildFooterNativeOverlay({
  accessibilityLabel,
  outdated,
  text,
}: DevBuildFooterStamp): { nativeFailed: boolean } {
  const [nativeFailed, setNativeFailed] = useState(false);
  // `hide` must be the last word: a `setStamp` enqueued moments earlier would otherwise land
  // after it and rebuild the window, leaving the overlay stuck on with nothing mounted to take
  // it down again. The native module suppresses stamps until `show` re-arms it here on mount.
  useEffect(() => {
    void nativeDevBuildOverlay?.show();
    return () => {
      void nativeDevBuildOverlay?.hide();
    };
  }, []);
  useEffect(() => {
    void nativeDevBuildOverlay?.setStamp(text, outdated, accessibilityLabel).catch(() => {
      setNativeFailed(true);
    });
  }, [text, outdated, accessibilityLabel]);
  return { nativeFailed };
}

export type DevBuildFooterFallbackGate = Tagged<
  { reason: "native-overlay-failed" | "no-native-overlay" },
  "DevBuildFooterFallbackGate"
>;

/**
 * Whether the React fallback draws the stamp: once hydrated (the stamp's clock text can't match
 * the server render), and only when the native overlay window can't — an old binary without the
 * module, Android / web, or a native `setStamp` failure. `null` = the native window draws it.
 */
function useDevBuildFooterFallbackGate({
  nativeFailed,
}: {
  nativeFailed: boolean;
}): DevBuildFooterFallbackGate | null {
  const hasHydrated = useHasHydrated();
  if (!hasHydrated) {
    return null;
  }
  if (nativeDevBuildOverlay === null) {
    return createGate<DevBuildFooterFallbackGate>({ reason: "no-native-overlay" });
  }
  return nativeFailed
    ? createGate<DevBuildFooterFallbackGate>({ reason: "native-overlay-failed" })
    : null;
}

const FOOTER_RESTING_BOTTOM_PX = 3;
/**
 * The gap between the floating search bar and the keyboard (`ABOVE_KEYBOARD_BOTTOM_GAP_PX`), which
 * the stamp fills exactly. Mirrors `StampTypography.inkHeight` in the native overlay.
 */
const FOOTER_INK_HEIGHT_PX = 9;
/** Monospace ink, tallest ascender to deepest descender, spans ~0.98em. */
const FOOTER_FONT_SIZE_PX = FOOTER_INK_HEIGHT_PX / 0.98;

/**
 * The on-screen keyboard's height while it is shown, 0 otherwise. Read from React Native's own
 * keyboard events rather than `KeyboardProvider`: the footer mounts outside the app providers so
 * it still shows on loading and error screens.
 */
function useKeyboardHeight(): number {
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  useEffect(() => {
    // Android only emits the `did` events.
    const showEvent = Platform.OS === "ios" ? "keyboardWillShow" : "keyboardDidShow";
    const hideEvent = Platform.OS === "ios" ? "keyboardWillHide" : "keyboardDidHide";
    const showSubscription = Keyboard.addListener(showEvent, (event) => {
      setKeyboardHeight(event.endCoordinates.height);
    });
    const hideSubscription = Keyboard.addListener(hideEvent, () => {
      setKeyboardHeight(0);
    });
    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, []);
  return keyboardHeight;
}

/** Migration fallback: an old binary cannot create the dedicated native window yet. */
function DevBuildFooterFallback({
  stamp: { accessibilityLabel, outdated, text },
}: {
  gate: DevBuildFooterFallbackGate;
  stamp: DevBuildFooterStamp;
}) {
  const keyboardHeight = useKeyboardHeight();
  return (
    <FallbackOverlay>
      <View className="absolute inset-0 z-2147483647 web:fixed" pointerEvents="none">
        <Text
          accessibilityLabel={accessibilityLabel}
          adjustsFontSizeToFit
          className="absolute inset-x-0 text-center font-mono text-neutral-500 ios:font-[Menlo] android:font-[monospace] web:top-1"
          numberOfLines={1}
          // Native sits above the keyboard; web stays at the top of the viewport.
          style={{
            bottom:
              Platform.OS === "web"
                ? undefined
                : keyboardHeight > 0
                  ? keyboardHeight
                  : FOOTER_RESTING_BOTTOM_PX,
            fontSize: FOOTER_FONT_SIZE_PX,
            lineHeight: FOOTER_INK_HEIGHT_PX,
          }}
          testID="dev-build-footer"
        >
          {text}
          {outdated ? <Text className="text-red-600"> ●</Text> : null}
        </Text>
      </View>
    </FallbackOverlay>
  );
}

/** Stays mounted while the footer is enabled: it owns the native overlay window's lifetime. */
function DevBuildFooterContent() {
  const stamp = useDevBuildFooterStamp();
  const { nativeFailed } = useDevBuildFooterNativeOverlay(stamp);
  const fallbackGate = useDevBuildFooterFallbackGate({ nativeFailed });
  return (
    <>{fallbackGate !== null && <DevBuildFooterFallback gate={fallbackGate} stamp={stamp} />}</>
  );
}

/**
 * The performance overlay (top center on web, bottom on native). Always on in development builds;
 * in staging / production it renders only when the user turns on "Show performance overlay" in Settings → Diagnostics
 * (`isPerformanceOverlayEnabled`, off by default), so nothing is mounted otherwise.
 */
export function DevBuildFooter() {
  const [isOverlayEnabled] = useDeviceSetting("isPerformanceOverlayEnabled");
  return __DEV__ || isOverlayEnabled ? <DevBuildFooterContent /> : null;
}
